/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import './AddEvent.less';
import PropTypes from 'prop-types';
import { Spin } from 'antd';
import { Actions, UserRoles, UserTypes } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { useSelector } from 'react-redux';
import useContacts from '../Hooks/useContacts';
import ListSchedule from './ListSchedule';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import useUserInfo from '../Hooks/useUserInfo';
import useStaffList from '../Hooks/useStaffList';
import useSchedule from '../Hooks/useSchedule';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { useLocation } from 'react-router-dom';
import { getTimeZoneFormat } from '@vantiva-sa/homesight_humaui/Utilities/DateTime';
import Task from '../Task/Task';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const toFloatingDayDate = (value) => {
  if (!value) return value;

  if (dayjs.isDayjs(value)) {
    return new Date(value.year(), value.month(), value.date(), 12, 0, 0, 0);
  }

  const dateValue = value instanceof Date ? value : new Date(value);
  return new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate(), 12, 0, 0, 0);
};

const Schedule = (props) => {
  // Destructure props
  const {
    onlyContent,
    hubRecord,
    eventRecord,
    userHubsData,
    rolename,
    onActionClick: parentActionClick,
    openSubMenu,
    activeKey,
    loginUserTimeformat,
    selectedUser,
    action,
    fetchDevices,
    principalRecord = {},
    contactOrStaffAction = false,
  } = props;

  const trans = useSelector(translator);
  const currentUser = useSelector(getCurrentUser);
  const [selectedTab, setSelectedTab] = useState('all'); // Manage selectedTab state in Schedule
  const handleSelectedTabChange = (tab) => {
    setSelectedTab(tab); // Update state when tab changes
  };

  const isStaff = useMemo(
    () =>
      hubRecord
        ? hubRecord?.rolename === UserRoles.staff || hubRecord?.staffadmin
        : currentUser?.usertype === UserTypes.staff,
    [hubRecord]
  );

  const useruuid = hubRecord?.useruuid ?? currentUser?.useruuid;
  const { data: contacts, isFetching: fetchingContacts } = useContacts({ useruuid });
  const { data: userInfo = {} } = useUserInfo({ useruuid });
  const { isFetching: fetchingStaffList } = useStaffList({
    useruuid: isStaff ? useruuid : null,
  });

  const userData = useSelector((user) => user.data.userInfo);
  const updatedUserInfo = userData[useruuid];
  const currentUserDataInfo = userData[openSubMenu ? hubRecord?.useruuid : useruuid];
  const userTimezone = getTimeZoneFormat(currentUserDataInfo?.timezone);

  const [fetchingScheduleList, scheduleList, fetchSchedule] = useSchedule(
    selectedTab,
    userTimezone
  );

  // Fallback to userInfo if loginUserTimeformat is undefined
  const loginUserTimeformatCheck = loginUserTimeformat || {
    is24hoursformat: updatedUserInfo?.is24hoursformat,
  };

  const [currentAction, setAction] = useState(action);
  const [record, setRecord] = useState(eventRecord);
  const [showModal, setShowModal] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [refetchListKey, setRefetchListKey] = useState(false);
  const calendarRangeRef = useRef(null);

  const toTimezoneDayStartDate = useCallback(
    (value) => {
      if (!value || !userTimezone) return value;
      return toFloatingDayDate(value);
    },
    [userTimezone]
  );

  const currentDate = useMemo(() => {
    if (!userTimezone) return null;
    return toFloatingDayDate(dayjs().tz(userTimezone));
  }, [userTimezone]);

  useEffect(() => {
    if (currentDate && !selectedDate) {
      setSelectedDate(currentDate);
    }
  }, [currentDate, selectedDate]);

  useEffect(() => setAction(action), [action]);
  useEffect(() => setRecord(eventRecord), [eventRecord]);

  // Re-fetch when tab changes — cache-friendly (different EventType = new cache key anyway)
  useEffect(() => {
    if (calendarRangeRef.current && userTimezone) {
      const { end, plusDays } = calendarRangeRef.current;
      fetchSchedule(useruuid, end, plusDays);
    }
  }, [selectedTab, userTimezone]);

  // Force re-fetch after add/edit/delete — always bypass cache
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (calendarRangeRef.current && userTimezone) {
      const { end, plusDays } = calendarRangeRef.current;
      fetchSchedule(useruuid, end, plusDays, true);
    }
  }, [refetchListKey]);

  const handleCalendarChange = useCallback(
    ({ start, end, view, date }) => {
      if (!userTimezone) return;
      if (view === 'day') {
        const normalizedDate = toTimezoneDayStartDate(date);
        setSelectedDate(normalizedDate);
        calendarRangeRef.current = { end: normalizedDate, plusDays: 0 };
        fetchSchedule(useruuid, normalizedDate, 0);
      } else if (view === 'week') {
        const normalizedEnd = toTimezoneDayStartDate(end);
        calendarRangeRef.current = { end: normalizedEnd, plusDays: 6 };
        fetchSchedule(useruuid, normalizedEnd, 6);
      } else {
        const msPerDay = 1000 * 60 * 60 * 24;
        const plusDays = Math.round((end - start) / msPerDay);
        const normalizedEnd = toTimezoneDayStartDate(end);
        calendarRangeRef.current = { end: normalizedEnd, plusDays };
        fetchSchedule(useruuid, normalizedEnd, plusDays);
      }
    },
    [useruuid, userTimezone, fetchSchedule, toTimezoneDayStartDate]
  );

  const reFetchList = () => {
    setRefetchListKey((prev) => !prev);
    setAction(null);
  };

  useEffect(
    () => {
      if (
        currentAction === Actions.viewTask ||
        currentAction === Actions.addTask ||
        currentAction === Actions.editTask ||
        currentAction === Actions.addReminderEvent ||
        currentAction === Actions.editReminderEvent ||
        currentAction === Actions.addEvent ||
        currentAction === Actions.editEvent ||
        currentAction === Actions.startEvent
      )
        setShowModal(true);
    },
    [eventRecord, record, currentAction],
    action
  );

  let { state } = useLocation();
  useEffect(() => {
    if (state != null) {
      state.action && setAction(state.action);
      state.eventRecord && setRecord(state.eventRecord);
    }
  }, [state]);

  const onActionClick =
    (action, record, refresh = false) =>
    () => {
      setAction(action);
      setRecord(record);
      setRefresh(refresh);
      if (
        action === Actions.viewTask ||
        action === Actions.addTask ||
        action === Actions.editTask ||
        action === Actions.addReminderEvent ||
        action === Actions.editReminderEvent ||
        action === Actions.addEvent ||
        action === Actions.editEvent ||
        action === Actions.startEvent
      )
        setShowModal(true);
      if (refresh && userTimezone) {
        const range = calendarRangeRef.current;
        if (range) {
          fetchSchedule(useruuid, range.end, range.plusDays, true);
        } else if (selectedDate) {
          fetchSchedule(useruuid, selectedDate, 0, true);
        }
      }
    };

  const closeModalHandler = (actionClose = false) => {
    setShowModal(false);
    if (actionClose) {
      onActionClick(null, null, true)();
    }
    // setRecord(null);
  };

  if (!userTimezone) {
    return (
      <div className="schedule-loading-container">
        <Spin tip="Loading timezone..." />
      </div>
    );
  }

  return (
    <>
      {!currentAction ||
      currentAction === Actions.viewTask ||
      currentAction === Actions.addTask ||
      currentAction === Actions.editTask ||
      currentAction === Actions.addReminderEvent ||
      currentAction === Actions.editReminderEvent ||
      currentAction === Actions.addEvent ||
      currentAction === Actions.editEvent ||
      currentAction === Actions.startEvent ? (
        <ListSchedule
          hubRecord={hubRecord}
          onlyContent={onlyContent}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          scheduleList={scheduleList}
          contacts={contacts}
          userInfo={userInfo}
          fetchingScheduleList={fetchingScheduleList}
          onActionClick={(activeKey ? null : parentActionClick) || onActionClick}
          rolename={rolename}
          openSubMenu={openSubMenu}
          loginUserTimeformat={loginUserTimeformatCheck}
          setParentSelectedTab={handleSelectedTabChange}
          fetchDevices={fetchDevices}
          selectedUser={selectedUser}
          contactOrStaffAction={contactOrStaffAction}
          onCalendarChange={handleCalendarChange}
        />
      ) : null}
      <Task
        onlyContent={
          currentAction === Actions.addEvent ||
          currentAction === Actions.editEvent ||
          currentAction === Actions.addTask ||
          currentAction === Actions.editTask ||
          currentAction === Actions.viewTask ||
          currentAction === Actions.addReminderEvent ||
          currentAction === Actions.editReminderEvent
        }
        action={currentAction}
        hubRecord={hubRecord}
        userInfo={userInfo}
        onActionClick={(activeKey ? null : parentActionClick) || onActionClick}
        loginUserTimeformat={loginUserTimeformatCheck}
        eventRecord={record}
        userHubsData={userHubsData}
        selectedSchedulelistDate={selectedDate}
        showModal={showModal}
        closeModalHandler={closeModalHandler}
        fetchSchedule={fetchSchedule}
        useruuid={useruuid}
        userTimezone={userTimezone}
        reFetchList={reFetchList}
        contacts={contacts}
        fetchingContacts={fetchingContacts}
        fetchingStaffList={fetchingStaffList}
        selectedUser={selectedUser}
        currentUserDataInfo={currentUserDataInfo}
        rolename={rolename}
        openSubMenu={openSubMenu}
        principalRecord={principalRecord}
        contactOrStaffAction={contactOrStaffAction}
      />
    </>
  );
};

Schedule.defaultProps = {
  onlyContent: false,
  selectedUser: '',
};

Schedule.propTypes = {
  onlyContent: PropTypes.bool,
  hubRecord: PropTypes.object,
  eventRecord: PropTypes.object,
  userHubsData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  rolename: PropTypes.string,
  onActionClick: PropTypes.func,
  openSubMenu: PropTypes.bool,
  activeKey: PropTypes.string,
  loginUserTimeformat: PropTypes.object,
  selectedUser: PropTypes.string,
  action: PropTypes.string,
  principalRecord: PropTypes.object,
};

export default Schedule;
